import { useMemo, useState } from 'react';
import { useT } from '@/i18n/use-t';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { extractApiError } from '@/lib/api-error';
import {
  useApplyCopy,
  useCopyPreview,
  useTerminalParamRegistry,
  type ImportApplyResult,
  type ImportPreview,
} from '@/hooks/use-terminal-params';
import { useTerminalProfiles } from '@/hooks/use-terminals';
import {
  applyExpectations,
  ImportPreviewTable,
  ImportTotals,
  keptRows,
} from './import-preview-table';
import { TIMING_STATEMENT } from './parameter-editor';

/**
 * Copy a parameter set between profiles (RL-284, TP-FR-027). A copy is an
 * import whose file is the source profile: the same preview, the same rule
 * that conflicts are resolved by being seen, the same apply (TP-FR-030).
 * Rendered by the editor whenever it is pinned to a profile, so it appears
 * on the Parameters page and on the profile page alike.
 */
export function CopyFromProfile({
  toProfileId,
  toProfileName,
}: {
  toProfileId: string;
  toProfileName: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [fromProfileId, setFromProfileId] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const profiles = useTerminalProfiles(open);
  const registry = useTerminalParamRegistry(open);
  const copyPreview = useCopyPreview();
  const applyCopy = useApplyCopy();

  const descriptors = useMemo(
    () => new Map((registry.data?.descriptors ?? []).map((d) => [d.key, d] as const)),
    [registry.data],
  );
  const sources = (profiles.data ?? []).filter((profile) => profile.id !== toProfileId);
  const kept = keptRows(preview, excluded);

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {t('parameters.copy.open', 'Copy from another profile…')}
      </Button>
    );
  }

  return (
    <section
      className="space-y-3 rounded-lg border bg-card p-4"
      aria-label={`${t('parameters.copy.title', 'Copy parameters onto')} ${toProfileName}`}
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold">
            {t('parameters.copy.title', 'Copy parameters onto')} {toProfileName}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t(
              'parameters.copy.intro',
              'Copies the source profile’s authored values. You see what will be added, changed and left alone before anything is written.',
            )}{' '}
            {t('parameters.editor.timing', TIMING_STATEMENT)}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setPreview(null);
            setResult(null);
            setError(null);
          }}
        >
          {t('common.close', 'Close')}
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor={`copy-source-${toProfileId}`}>
            {t('parameters.copy.sourceProfile', 'Source profile')}
          </Label>
          <Select
            id={`copy-source-${toProfileId}`}
            className="w-64"
            value={fromProfileId}
            disabled={profiles.isLoading || copyPreview.isPending}
            onChange={(event) => {
              setFromProfileId(event.target.value);
              setPreview(null);
              setResult(null);
              setError(null);
            }}
          >
            <option value="">{t('parameters.page.chooseProfile', '— choose a profile —')}</option>
            {sources.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={fromProfileId === '' || copyPreview.isPending}
          onClick={async () => {
            setError(null);
            setResult(null);
            setExcluded(new Set());
            try {
              setPreview(await copyPreview.mutateAsync({ fromProfileId, toProfileId }));
            } catch (cause) {
              setError(extractApiError(cause));
            }
          }}
        >
          {copyPreview.isPending
            ? t('parameters.copy.previewing', 'Previewing…')
            : t('parameters.copy.preview', 'Preview copy')}
        </Button>
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {preview !== null && (
        <div className="space-y-3">
          {preview.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                'parameters.copy.noValues',
                'The source profile has no authored parameters to copy.',
              )}
            </p>
          ) : (
            <>
              <ImportTotals totals={preview.totals} />
              <ImportPreviewTable
                preview={preview}
                descriptors={descriptors}
                excluded={excluded}
                onToggle={(address, included) =>
                  setExcluded((current) => {
                    const next = new Set(current);
                    if (included) next.delete(address);
                    else next.add(address);
                    return next;
                  })
                }
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  disabled={kept.length === 0 || applyCopy.isPending}
                  onClick={async () => {
                    setError(null);
                    try {
                      const applied = await applyCopy.mutateAsync({
                        fromProfileId,
                        toProfileId,
                        keys: kept.map((row) => row.key),
                        expectedRows: applyExpectations(kept),
                      });
                      setResult(applied);
                      setPreview(null);
                    } catch (cause) {
                      setError(extractApiError(cause));
                    }
                  }}
                >
                  {applyCopy.isPending
                    ? t('parameters.copy.copying', 'Copying…')
                    : `${t('parameters.copy.copy', 'Copy')} ${kept.length} ${
                        kept.length === 1
                          ? t('parameters.copy.parameter', 'parameter')
                          : t('parameters.copy.parameters', 'parameters')
                      }`}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {t(
                    'parameters.copy.leftAloneBefore',
                    'Unticked and unchanged rows, and everything else on',
                  )}{' '}
                  {toProfileName}
                  {t('parameters.copy.leftAloneAfter', ', are left alone.')}
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {result !== null && (
        <p className="text-sm" role="status">
          {t('parameters.copy.copied', 'Copied')} {result.applied}{' '}
          {result.applied === 1
            ? t('parameters.copy.parameter', 'parameter')
            : t('parameters.copy.parameters', 'parameters')}{' '}
          {t('parameters.copy.onto', 'onto')} {toProfileName}.{' '}
          {t('parameters.editor.timing', TIMING_STATEMENT)}
        </p>
      )}
    </section>
  );
}
